import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useRestaurantContext } from "@/contexts/RestaurantContext";
import { toast } from "sonner";
import type { JobType } from "@/hooks/usePrinters";

export interface ProductionLocation {
  id: string;
  restaurant_id: string;
  name: string;
  printer_name: string;
  printer_id: string | null;
  is_active: boolean;
  job_type: JobType | null;
  created_at: string;
  updated_at: string;
  // Joined printer data
  printers?: {
    id: string;
    name: string;
    printer_name: string;
  } | null;
}

export interface ProductionLocationFormData {
  name: string;
  printer_name: string;
  printer_id?: string | null;
  is_active: boolean;
  job_type?: JobType | null;
}

export function useProductionLocations() {
  const [locations, setLocations] = useState<ProductionLocation[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const { activeRestaurant } = useRestaurantContext();

  const fetchLocations = useCallback(async () => {
    if (!activeRestaurant) {
      setLocations([]);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    const { data, error } = await supabase
      .from("production_locations")
      .select("*, printers(id, name, printer_name), kds_station_config(sla_warning_seconds, sla_critical_seconds)")
      .eq("restaurant_id", activeRestaurant.id)
      .order("name");

    if (error) {
      console.error("Error fetching production locations:", error);
      toast.error("Erro ao carregar locais de produção");
    } else {
      setLocations(data as ProductionLocation[]);
    }
    setIsLoading(false);
  }, [activeRestaurant]);

  useEffect(() => {
    fetchLocations();
  }, [fetchLocations]);

  const createLocation = async (formData: ProductionLocationFormData) => {
    if (!activeRestaurant) return null;

    const { data, error } = await supabase
      .from("production_locations")
      .insert({
        restaurant_id: activeRestaurant.id,
        name: formData.name,
        printer_name: formData.printer_name,
        printer_id: formData.printer_id || null,
        is_active: formData.is_active,
        job_type: formData.job_type || null,
      })
      .select("*, printers(id, name, printer_name)")
      .single();

    if (error) {
      console.error("Error creating production location:", error);
      toast.error("Erro ao criar local de produção");
      return null;
    }

    toast.success("Local de produção criado com sucesso!");
    await fetchLocations();
    return data;
  };

  const updateLocation = async (id: string, formData: Partial<ProductionLocationFormData>) => {
    const { error } = await supabase
      .from("production_locations")
      .update(formData)
      .eq("id", id);

    if (error) {
      console.error("Error updating production location:", error);
      toast.error("Erro ao atualizar local de produção");
      return false;
    }

    toast.success("Local de produção atualizado!");
    await fetchLocations();
    return true;
  };

  const deleteLocation = async (id: string) => {
    const { error } = await supabase
      .from("production_locations")
      .delete()
      .eq("id", id);

    if (error) {
      console.error("Error deleting production location:", error);
      toast.error("Erro ao excluir local de produção");
      return false;
    }

    toast.success("Local de produção excluído!");
    await fetchLocations();
    return true;
  };

  const toggleActive = async (id: string) => {
    const location = locations.find((l) => l.id === id);
    if (!location) return false;

    return updateLocation(id, { is_active: !location.is_active });
  };

  const getActiveLocations = () => {
    return locations.filter((l) => l.is_active);
  };

  return {
    locations,
    isLoading,
    fetchLocations,
    createLocation,
    updateLocation,
    deleteLocation,
    toggleActive,
    getActiveLocations,
  };
}
